import { Children, ReactElement, ReactNode, isValidElement, useMemo, useState } from 'react';
import { Box, Button, Chip, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useSearchParams } from 'react-router-dom';
import { FilterCard } from '../../../components/Mui/filter-card';
import { PAGE_FIELD_NAME } from 'shared/hooks/paging-params';

export type SummaryFormatterContext = {
  values: string[];
  rawValues: string[];
  formattedValues: string[];
  keys: string[];
  searchParams: URLSearchParams;
};

export type ValueFormatterContext = {
  value: string;
  key: string;
  searchParams: URLSearchParams;
};

export type FilterWrapperItemProps = {
  label: string;
  queryKeys: string | string[];
  children: ReactNode;
  summaryFormatter?: (context: SummaryFormatterContext) => string;
  popoverWidth?: number | string;
  valueFormatter?: (context: ValueFormatterContext) => string;
};

type RangeSummaryFormatterParams = {
  fromKey: string;
  toKey: string;
  emptyLabel: string;
  activeLabel?: string;
};

export const createRangeSummaryFormatter = ({ fromKey, toKey, emptyLabel, activeLabel = emptyLabel }: RangeSummaryFormatterParams) => {
  return ({ searchParams }: SummaryFormatterContext) => {
    const from = searchParams.get(fromKey);
    const to = searchParams.get(toKey);
    if (!from && !to) return emptyLabel;
    if (from && to) return `${activeLabel}: ${from} — ${to}`;
    return `${activeLabel}: ${from || to}`;
  };
};

type FilterWrapperProps = {
  children: ReactNode;
  title?: string;
  sticky?: boolean;
  allowReset?: boolean;
  dense?: boolean;
  withCard?: boolean;
  showHeader?: boolean;
  totalCount?: number;
};

const FILTER_ITEM_NAME = 'FilterWrapperItem';

const FilterWrapperItem = ({ children }: FilterWrapperItemProps) => <>{children}</>;
FilterWrapperItem.displayName = FILTER_ITEM_NAME;

const toArray = (value: string | string[]) => (Array.isArray(value) ? value : [value]);

const truncateValue = (value: string, length = 16) => (value.length > length ? `${value.slice(0, length)}…` : value);

const defaultSummary = (label: string, values: string[]) => {
  if (!values.length) return label;
  if (values.length === 1) return `${label}: ${truncateValue(values[0])}`;
  return `${label}: ${truncateValue(values[0])} (+${values.length - 1})`;
};

const FilterWrapperBase = ({
  title = 'Фильтры',
  children,
  sticky = true,
  allowReset = true,
  dense = false,
  withCard = true,
  showHeader = true,
  totalCount,
}: FilterWrapperProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openItem, setOpenItem] = useState<{ index: number; anchor: HTMLElement } | null>(null);
  const [rehydrationKey, setRehydrationKey] = useState(0);

  const items = useMemo(
    () =>
      Children.toArray(children).filter((child): child is ReactElement<FilterWrapperItemProps> => isValidElement(child) && (child.type as any).displayName === FILTER_ITEM_NAME),
    [children],
  );

  const itemStates = useMemo(
    () =>
      items.map((item) => {
        const keys = toArray(item.props.queryKeys);

        const rawEntries = keys.flatMap((key) => {
          const multiple = searchParams.getAll(key);
          if (multiple.length) return multiple.map((value) => ({ key, value }));
          const single = searchParams.get(key);
          return single ? [{ key, value: single }] : [];
        });

        const rawValues = rawEntries.map((entry) => entry.value);
        const normalizedEntries = rawEntries.flatMap(({ key, value }) =>
          value
            .split(',')
            .map((part) => part.trim())
            .filter((part) => part !== '' && part !== 'null' && part !== 'undefined')
            .map((part) => ({ key, value: part })),
        );

        const values = normalizedEntries.map((entry) => entry.value);
        const formattedValues = normalizedEntries.map((entry) => (item.props.valueFormatter ? item.props.valueFormatter({ ...entry, searchParams }) : entry.value));
        const active = values.length > 0;

        const summary = item.props.summaryFormatter
          ? item.props.summaryFormatter({ values, rawValues, formattedValues, keys, searchParams })
          : defaultSummary(item.props.label, formattedValues);

        return {
          label: item.props.label,
          keys,
          active,
          summary,
          content: item.props.children,
          popoverWidth: item.props.popoverWidth,
        };
      }),
    [items, searchParams],
  );

  const hasActiveFilters = itemStates.some((state) => state.active);
  const activeFiltersCount = itemStates.filter((state) => state.active).length;

  const clearKeys = (keys: string[]) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        keys.forEach((key) => params.delete(key));
        params.delete(PAGE_FIELD_NAME);
        return params;
      },
      { replace: true },
    );
    setRehydrationKey((key) => key + 1);
    setOpenItem(null);
  };

  const handleResetAll = () => {
    const allKeys = itemStates.flatMap((state) => state.keys);
    clearKeys(allKeys);
  };

  const handleToggle = (index: number, anchor: HTMLElement) => {
    setOpenItem((prev) => {
      if (prev?.index === index) return null;
      return { index, anchor };
    });
  };

  const handleClose = () => setOpenItem(null);

  const content = (
    <>
      {showHeader && (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={0.75}>
            <FilterListIcon fontSize="small" color={hasActiveFilters ? 'primary' : 'inherit'} />
            <Typography variant="subtitle2" fontWeight={600}>
              {title}
            </Typography>
            {hasActiveFilters && <Chip label={`${activeFiltersCount}`} size="small" color="primary" variant="outlined" />}
          </Box>
          {totalCount !== undefined && (
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              {`Итого - ${totalCount}`}
            </Typography>
          )}
          <Box flexGrow={1} />
          {allowReset && (
            <Button size="small" startIcon={<FilterAltOffIcon fontSize="small" />} onClick={handleResetAll} disabled={!hasActiveFilters}>
              Сбросить все
            </Button>
          )}
        </Box>
      )}

      <Box display="flex" flexWrap="wrap" gap={dense ? 0.5 : 1} mt={showHeader ? (dense ? 1 : 1.5) : 0}>
        {itemStates.map((state, index) => {
          const open = openItem?.index === index;
          return (
            <Box key={`${state.label}-${index}-${rehydrationKey}`} display="inline-flex">
              <Tooltip title={state.summary}>
                <span>
                  <Button
                    variant={state.active ? 'contained' : 'outlined'}
                    color={state.active ? 'primary' : 'inherit'}
                    size="small"
                    endIcon={<ExpandMoreIcon fontSize="small" />}
                    onClick={(event) => handleToggle(index, event.currentTarget)}
                  >
                    {state.summary}
                  </Button>
                </span>
              </Tooltip>
              <Popover
                open={open}
                anchorEl={open ? openItem?.anchor : null}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                keepMounted
                PaperProps={{
                  sx: {
                    p: 2,
                    width: state.popoverWidth ?? 360,
                    maxWidth: '95vw',
                    borderRadius: 2,
                  },
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {state.label}
                  </Typography>
                  <IconButton size="small" onClick={handleClose}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {state.content}
                </Box>
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Button size="small" color="inherit" startIcon={<FilterAltOffIcon fontSize="small" />} onClick={() => clearKeys(state.keys)} disabled={!state.active}>
                    Очистить
                  </Button>
                  <Button size="small" variant="contained" onClick={handleClose}>
                    Готово
                  </Button>
                </Box>
              </Popover>
            </Box>
          );
        })}
      </Box>
    </>
  );

  return (
    <Box
      sx={
        withCard
          ? {
              position: sticky ? 'sticky' : 'static',
              left: 0,
              right: 0,
              top: 0,
              zIndex: 10,
              backgroundColor: '#fff',
            }
          : { position: 'static' }
      }
    >
      {withCard ? <FilterCard>{content}</FilterCard> : content}
    </Box>
  );
};

type FilterWrapperComponent = ((props: FilterWrapperProps) => JSX.Element) & {
  Item: (props: FilterWrapperItemProps) => JSX.Element;
};

export const FilterWrapper = Object.assign(FilterWrapperBase, {
  Item: FilterWrapperItem,
}) as FilterWrapperComponent;
